import React, {
  ReactElement,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';

import ReactDOM from 'react-dom';
import Geocode from 'react-geocode';
import { Spinner } from 'react-bootstrap';
import { GOOGLE_DEVELOPER_KEY } from '../../utils/constants';
import useOutsideClickAlerter from '../../hooks/useOutsideClickAlerter';

Geocode.setApiKey(GOOGLE_DEVELOPER_KEY);

interface Props {
  showStaticForm?: boolean,
  onInitiateSearch?: () => void,
  onCompletionCallback?: () => void,
  onErrorCallback?: () => void,
}

const SetLocation: React.FC<Props> = ({
  showStaticForm,
  onInitiateSearch,
  onCompletionCallback,
  onErrorCallback,
}): ReactElement => {
  function initLocation(): string|null {
    if (localStorage.getItem('location')) {
      return localStorage.getItem('location');
    }
    return 'Set Location';
  }
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [loc, setLoc] = useState(initLocation());
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (hasError && onErrorCallback) {
      onErrorCallback();
    }
  }, [hasError, onErrorCallback]);

  const onOutsideClick = useCallback(() => {
    if (!isLoading && showTooltip) {
      setShowTooltip(false);
    }
  }, [isLoading, showTooltip]);

  const wrapperRef = useRef(null);
  useOutsideClickAlerter(wrapperRef, onOutsideClick);

  async function showLocation(lat: any, lng: any): Promise<string> {
    if (lat && lng) {
      const location = Geocode.fromLatLng(lat, lng).then(
        (response: { plus_code: { compound_code: any; }; }) => {
          let address = response.plus_code.compound_code;
          address = address.split(' ').slice(1).join(' '); // Removes serialized code from front of string
          return address;
        },
        (_error: any) => {
          setHasError(true);
          return 'Location Not Found';
        },
      ).catch((e) => {
        setHasError(true);
        setIsLoading(false);
      });
      return location;
    }
    return 'Set Location';
  }

  const setLocation = (latitude: any, longitude: any, event?: Event) => {
    if (latitude !== localStorage.getItem('latitude') || longitude !== localStorage.getItem('longitude')) {
      localStorage.setItem('latitude', latitude);
      localStorage.setItem('longitude', longitude);
      if (event && event.target) {
        // Fire event to let other components that use location know that it has been updated
        event.target.dispatchEvent(new CustomEvent('updateLocation', {
          bubbles: true,
          detail: {
            newLocation: true,
          },
        }));
      }
      showLocation(latitude, longitude).then((location) => {
        setLoc(location);
        localStorage.setItem('location', location);
        if (event && event.target) {
          // Fire event to let other components know location name has been updated
          event.target.dispatchEvent(new CustomEvent('updateLocationName', {
            bubbles: true,
            detail: {
              newLocationName: location,
            },
          }));
        }
        if (onCompletionCallback) {
          onCompletionCallback();
        }
        setIsLoading(false);
        setHasError(false);
        setShowTooltip(false);
      }).catch((e) => {
        setHasError(true);
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
      setHasError(false);
      setShowTooltip(false);
    }
  };

  const updateLocationName = useCallback((e: any) => {
    const { detail } = e as { detail: { newLocationName: any } };
    if (detail && detail.newLocationName) {
      const newLocName = detail.newLocationName;
      if (typeof newLocName !== 'string') {
        setHasError(true);
        setLoc('Location Not Found');
      } else if (newLocName !== loc) {
        setLoc(newLocName);
      }
    }
  }, [loc, setLoc]);

  useEffect(() => {
    document.addEventListener('updateLocationName', updateLocationName);
    return () => document.removeEventListener('updateLocationName', updateLocationName);
  }, [updateLocationName]);

  const getCoords = async (cityName:any) => {
    if (cityName) {
      // Prevents user from entering a zipcode
      if (/\b\d{5}\b/.test(cityName.toString())) {
        setHasError(true);
        setIsLoading(false);
        return false;
      }
      const pos:any = Geocode.fromAddress(cityName).then(
        (response) => {
          const latitude = response.results[0].geometry.location.lat;
          const longitude = response.results[0].geometry.location.lng;
          return [
            latitude,
            longitude,
          ];
        },
        (_error) => {
          setHasError(true);
          return false;
        },
      ).catch((e) => {
        setHasError(true);
        setIsLoading(false);
      });
      return pos;
    } if (navigator.geolocation) {
      const pos:any = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });
      return [
        pos.coords.latitude,
        pos.coords.longitude,
      ];
    }
    return false;
  };

  const getLocation = (event) => {
    getCoords(false).then((coords:any) => {
      if (coords) {
        setLocation(coords[0], coords[1], event);
      } else {
        setIsLoading(false);
        setHasError(true);
      }
    }).catch((e) => {
      setHasError(true);
      setIsLoading(false);
    });
  };

  const setCityName = (event) => {
    const formData = new FormData(event.target);
    const cityName = formData.get('city_name');
    if (cityName) {
      getCoords(cityName).then((coords:any) => {
        if (coords) {
          setLocation(coords[0], coords[1], event);
        } else {
          setIsLoading(false);
          setHasError(true);
        }
        return true;
      }).catch((e) => {
        setHasError(true);
        setIsLoading(false);
      });
    }
  };

  // TODO: write explanation
  useEffect(() => {
    window.addEventListener('resize', () => {
      setLoc(initLocation());
    });
  }, []);

  if (showStaticForm) {
    return (
      <div className="set-location-tooltip">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (!isLoading) {
              if (onInitiateSearch) {
                onInitiateSearch();
              }
              setIsLoading(true);
              getLocation(e);
            }
          }}
          className="m-0 text-center text-nowrap position-relative set-location"
        >
          <i className="fas fa-map-marker-alt mr-1" />
          Use Current Location
        </a>
        <p className="text-center text-uppercase">&#8212; Or &#8212;</p>
        <form
          id="location-submit-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (onInitiateSearch) {
              onInitiateSearch();
            }
            setIsLoading(true);
            setCityName(event);
          }}
        >
          <div className="font-weight-bold text-black">
            <p>Enter your city name</p>
            <div className="d-flex">
              <input
                aria-label="Enter city name here to search by location."
                className="form-control mr-1"
                type="text"
                placeholder="City name"
                name="city_name"
              />
              <button
                disabled={isLoading}
                className="btn btn-primary"
                type="submit"
              >
                Submit
              </button>
            </div>
          </div>
          {hasError && (
            <p className="text-center text-danger">Something went wrong; check your connection and try again.</p>
          )}
        </form>
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="position-relative set-location"
    >
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          setShowTooltip(true);
        }}
        className="m-0 text-center text-nowrap btn text-primary location"
      >
        <small>
          <i className="fas fa-map-marker-alt mr-1" />
          {loc}
        </small>
      </a>
      {showTooltip && (
        <div className="set-location-tooltip">
          {
            isLoading && (
              <div className="loading-overlay">
                <Spinner animation="border" variant="primary" />
              </div>
            )
          }
          <form
            id="location-submit-form-tooltip"
            onSubmit={(event) => {
              event.preventDefault();
              if (onInitiateSearch) {
                onInitiateSearch();
              }
              setIsLoading(true);
              setCityName(event);
            }}
          >
            <button
              type="button"
              className="close"
              disabled={isLoading}
              onClick={() => setShowTooltip(false)}
            >
              <span aria-hidden="true">&times;</span>
            </button>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (!isLoading) {
                  if (onInitiateSearch) {
                    onInitiateSearch();
                  }
                  setIsLoading(true);
                  getLocation(e);
                }
              }}
              className="m-0 text-center text-nowrap position-relative set-location"
            >
              <small>
                Use Current Location
              </small>
            </a>
            <p>
              <small>
                Or
              </small>
            </p>
            <div className="font-weight-bold text-black">
              <small>
                Enter your city name
                <span className="d-flex">
                  <input
                    className="form-control mr-1"
                    type="text"
                    placeholder="City name"
                    name="city_name"
                  />
                  <button
                    disabled={isLoading}
                    className="btn btn-primary"
                    type="submit"
                  >
                    Submit
                  </button>
                </span>
              </small>
            </div>
            {hasError && (
              <p className="text-center text-danger">
                <small>Something went wrong; check your connection and try again.</small>
              </p>
            )}
          </form>
        </div>
      )}
    </div>
  );
};

if (document.getElementsByClassName('react-set-location').length > 0) {
  document.querySelectorAll('.react-set-location').forEach((element) => {
    ReactDOM.render(<SetLocation />, element);
  });
}

export default SetLocation;
